import { useEffect, useState } from "react";
import { Check, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { fmtDate, fmtMoney, todayISO } from "../utils/format";

export default function Reservations() {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [convertFor, setConvertFor] = useState(null);
  const [newCustomer, setNewCustomer] = useState(false);
  const [form, setForm] = useState({ customer_id: "", new_customer: { name: "", phone: "" }, vehicle_id: "", date: todayISO(), agreed_price: "", deposit: "", deadline: "", salesperson: "", comment: "", account_id: "" });
  const [convForm, setConvForm] = useState({ paid_amount: "", discount: "0", payment_method: "Espèces", account_id: "", installments_count: "1", due_date: "" });
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const delResa = async (r) => {
    if (!window.confirm(`Supprimer la réservation ${r.reservation_number} ?${r.status === "Active" ? " Le véhicule sera libéré." : ""}`)) return;
    try {
      await api.delete(`/reservations/${r.id}`);
      toast.success("Réservation supprimée");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const load = () => api.get("/reservations").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reservable = vehicles.filter((v) => ["Disponible", "Dédouané", "En préparation"].includes(v.status));

  const submit = async () => {
    if (!form.vehicle_id) return toast.error("Véhicule requis");
    if (!newCustomer && !form.customer_id) return toast.error("Client requis");
    try {
      await api.post("/reservations", { ...form, agreed_price: Number(form.agreed_price) || 0, deposit: Number(form.deposit) || 0, account_id: form.account_id || null, deadline: form.deadline || null, customer_id: newCustomer ? null : form.customer_id, new_customer: newCustomer ? form.new_customer : null });
      toast.success("Réservation créée — véhicule marqué Réservé");
      setOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const convert = async () => {
    try {
      const r = await api.post(`/reservations/${convertFor.id}/convert`, { ...convForm, paid_amount: Number(convForm.paid_amount) || 0, discount: Number(convForm.discount) || 0, account_id: convForm.account_id || null, due_date: convForm.due_date || null, installments_count: Number(convForm.installments_count) || 1 });
      toast.success(`Réservation convertie en vente ${r.sale_number}`);
      setConvertFor(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const cancel = async (r) => {
    if (!window.confirm(`Annuler la réservation ${r.reservation_number} ?`)) return;
    await api.post(`/reservations/${r.id}/cancel`);
    toast.success("Réservation annulée — véhicule libéré");
    load();
  };

  return (
    <div className="space-y-5" data-testid="reservations-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Réservations</h1>
          <p className="mt-1 text-sm text-slate-500">Réservation avec acompte — alerte automatique avant expiration.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-reservation-button"><Plus className="mr-1 h-4 w-4" /> Nouvelle réservation</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nouvelle réservation</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 flex items-center justify-between">
                <Label>Client *</Label>
                <button type="button" className="text-xs font-medium text-cyan-600 hover:underline" onClick={() => setNewCustomer(!newCustomer)} data-testid="resa-toggle-new-customer">
                  {newCustomer ? "Client existant" : "+ Nouveau client"}
                </button>
              </div>
              {newCustomer ? (
                <>
                  <div><Label>Nom</Label><Input value={form.new_customer.name} onChange={(e) => set("new_customer", { ...form.new_customer, name: e.target.value })} data-testid="resa-new-customer-name" /></div>
                  <div><Label>Téléphone</Label><Input value={form.new_customer.phone} onChange={(e) => set("new_customer", { ...form.new_customer, phone: e.target.value })} /></div>
                </>
              ) : (
                <div className="col-span-2">
                  <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
                    <SelectTrigger data-testid="resa-customer-select"><SelectValue placeholder="Choisir un client…" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.phone}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2"><Label>Véhicule *</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => {
                  const veh = vehicles.find((x) => x.id === v);
                  setForm((f) => ({ ...f, vehicle_id: v, agreed_price: veh?.target_sale_price ? String(veh.target_sale_price) : f.agreed_price }));
                }}>
                  <SelectTrigger data-testid="resa-vehicle-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{reservable.map((v) => <SelectItem key={v.id} value={v.id}>{v.internal_number} — {v.brand} {v.model}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
              <div><Label>Date limite</Label><Input type="date" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} data-testid="resa-deadline" /></div>
              <div><Label>Prix convenu (FCFA)</Label><Input type="number" value={form.agreed_price} onChange={(e) => set("agreed_price", e.target.value)} data-testid="resa-price" /></div>
              <div><Label>Acompte (FCFA)</Label><Input type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} data-testid="resa-deposit" /></div>
              <div><Label>Commercial</Label><Input value={form.salesperson} onChange={(e) => set("salesperson", e.target.value)} /></div>
              <div><Label>Compte (acompte)</Label>
                <Select value={form.account_id} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Commentaire</Label><Textarea rows={2} value={form.comment} onChange={(e) => set("comment", e.target.value)} /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="resa-submit">Créer la réservation</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable testidPrefix="reservations-table" rows={rows}
        searchKeys={["reservation_number", "customer_name", "vehicle_label"]}
        columns={[
          { key: "reservation_number", label: "N°", render: (r) => <span className="font-mono-num font-semibold">{r.reservation_number}</span> },
          { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
          { key: "customer_name", label: "Client" },
          { key: "vehicle_label", label: "Véhicule" },
          { key: "agreed_price", label: "Prix convenu", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.agreed_price)}</span> },
          { key: "deposit", label: "Acompte", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.deposit)}</span> },
          { key: "deadline", label: "Date limite", render: (r) => {
            const overdue = r.deadline && new Date(r.deadline) < new Date(todayISO()) && r.status === "Active";
            return <span className={overdue ? "font-semibold text-red-600" : ""}>{fmtDate(r.deadline)}</span>;
          } },
          { key: "status", label: "Statut", render: (r) => <StatusBadge status={r.status} /> },
          { key: "actions", label: "", sortable: false, render: (r) => (
            <div className="flex gap-1">
              {r.status === "Active" && (
                <>
                  <Button size="sm" data-testid={`convert-${r.reservation_number}`} onClick={() => { setConvertFor(r); setConvForm({ paid_amount: "", discount: "0", payment_method: "Espèces", account_id: "", installments_count: "1", due_date: "" }); }}>
                    <Check className="mr-1 h-3.5 w-3.5" /> Convertir
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500" data-testid={`cancel-${r.reservation_number}`} onClick={() => cancel(r)}><X className="h-4 w-4" /></Button>
                </>
              )}
              {isAdmin && <Button size="sm" variant="ghost" className="text-red-500" title="Supprimer" onClick={() => delResa(r)} data-testid={`delete-resa-${r.reservation_number}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ) },
        ]}
      />

      <Dialog open={!!convertFor} onOpenChange={() => setConvertFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Convertir en vente — {convertFor?.reservation_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Prix convenu : <span className="font-mono-num font-bold">{fmtMoney(convertFor?.agreed_price)}</span> · Acompte déjà versé : <span className="font-mono-num font-bold text-emerald-600">{fmtMoney(convertFor?.deposit)}</span></p>
            <div><Label>Paiement complémentaire (FCFA)</Label><Input type="number" value={convForm.paid_amount} onChange={(e) => setConvForm({ ...convForm, paid_amount: e.target.value })} data-testid="convert-paid" /></div>
            <div><Label>Remise (FCFA)</Label><Input type="number" value={convForm.discount} onChange={(e) => setConvForm({ ...convForm, discount: e.target.value })} /></div>
            <div><Label>Compte crédité</Label>
              <Select value={convForm.account_id} onValueChange={(v) => setConvForm({ ...convForm, account_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nombre d'échéances (si crédit)</Label><Input type="number" min="1" value={convForm.installments_count} onChange={(e) => setConvForm({ ...convForm, installments_count: e.target.value })} /></div>
            <div><Label>Première échéance</Label><Input type="date" value={convForm.due_date} onChange={(e) => setConvForm({ ...convForm, due_date: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={convert} data-testid="convert-submit">Confirmer la vente</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
